/**
 * PI Calculator Utility Functions
 * Handles API communication and score calculation for the Promoting Interoperability calculator
 */

import { invalidatePracticeScoring } from '@/lib/query-keys';

// Get API URL for database calls
const getApiUrl = () => {
  return import.meta.env.VITE_API_URL || 'http://localhost:3001';
};

// Get auth token for API calls
const getAuthToken = () => {
  return localStorage.getItem('auth_token');
};

// ============================================================================
// Types & Interfaces
// ============================================================================

export interface PICalculatorData {
  id?: number;
  practice_id?: number;
  year?: number;
  npi?: string | null;
  reporting_level?: 'group' | 'individual';
  
  // Reporting Context
  performance_start?: string | null;
  performance_end?: string | null;
  cehrt_id?: string | null;
  
  // Prerequisites
  pi_pphi_1?: boolean | null; // Security Risk Analysis
  pi_pphi_2?: boolean | null; // SAFER Guide Assessment
  pi_infblo_1?: boolean | null; // No Interoperability Restrictions
  pi_oncdir_1?: boolean | null; // ONC Direct Review Acknowledgment
  
  // e-Prescribing Objective
  pi_ep_1_numerator?: number | null;
  pi_ep_1_denominator?: number | null;
  pi_lvpp_1?: boolean; // e-Prescribing exclusion
  pi_ep_2?: boolean | null; // Query of PDMP attestation
  pi_ep_2_exclusion?: 'EX_1' | 'EX_2' | null;
  
  // Provider to Patient Exchange
  pi_pea_1_numerator?: number | null;
  pi_pea_1_denominator?: number | null;
  
  // Health Information Exchange
  hie_option?: 1 | 2 | 3 | null;
  pi_hie_1_numerator?: number | null;
  pi_hie_1_denominator?: number | null;
  pi_lvotc_1?: boolean; // HIE Sending exclusion
  pi_hie_4_numerator?: number | null;
  pi_hie_4_denominator?: number | null;
  pi_lvitc_2?: boolean; // HIE Receiving exclusion
  pi_hie_5?: boolean | null; // Bi-Directional Exchange
  pi_hie_6?: boolean | null; // TEFCA Exchange
  
  // Public Health - Immunization
  pi_phcdrr_1?: boolean | null;
  pi_phcdrr_1_level?: 'PRE' | 'PROD' | null;
  pi_phcdrr_1_exclusion?: 'EX_1' | 'EX_2' | 'EX_3' | null;
  
  // Public Health - Bonus
  pi_phcdrr_2?: boolean | null; // Syndromic Surveillance
  pi_phcdrr_2_level?: 'PRE' | 'PROD' | null;
  pi_phcdrr_4?: boolean | null; // Public Health Registry
  pi_phcdrr_4_level?: 'PRE' | 'PROD' | null;
  pi_phcdrr_5?: boolean | null; // Clinical Data Registry
  pi_phcdrr_5_level?: 'PRE' | 'PROD' | null;
  
  // Cached scores
  calculated_score?: number;
  bonus_points?: number;
  total_score?: number;
  
  // Metadata
  created_at?: string;
  updated_at?: string;
}

export interface PIScoreBreakdown {
  points: number;
  maxPoints: number;
}

export interface PICalculatedScore {
  prerequisitesPassed: boolean;
  ePrescribingScore: number;
  queryPdmpScore: number;
  patientAccessScore: number;
  hieScore: number;
  publicHealthScore: number;
  bonusPoints: number;
  totalScore: number;
  maxPossibleScore: number;
  breakdown: {
    ePrescribing: PIScoreBreakdown;
    queryPdmp: PIScoreBreakdown;
    patientAccess: PIScoreBreakdown;
    hie: PIScoreBreakdown;
    publicHealth: PIScoreBreakdown;
    bonus: PIScoreBreakdown;
  };
}

export interface PICalculatorResponse extends PICalculatorData {
  calculatedScore: PICalculatedScore;
}

export interface PIBulkUploadResult {
  success: boolean;
  processed: number;
  errors: number;
  results: { npi: string; score: number; success: boolean }[];
  errorDetails: { row: PICalculatorData; error: string }[];
}

// ============================================================================
// API Functions
// ============================================================================

/**
 * Fetch PI calculator data for a practice (group or specific NPI)
 */
export async function getPICalculatorData(
  practiceId: string,
  npi?: string,
  year: number = 2025
): Promise<PICalculatorResponse | null> {
  try {
    const apiUrl = getApiUrl();
    const token = getAuthToken();
    
    let url = `${apiUrl}/api/practices/${practiceId}/pi-calculator?year=${year}`;
    if (npi) {
      url += `&npi=${npi}`;
    }
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch PI calculator data: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching PI calculator data:', error);
    return null;
  }
}

/**
 * Save PI calculator data (auto-save)
 * Throws an error if the save fails so the caller can handle it appropriately
 */
export async function savePICalculatorData(
  practiceId: string,
  data: PICalculatorData,
  year: number = 2025
): Promise<PICalculatorResponse> {
  const apiUrl = getApiUrl();
  const token = getAuthToken();
  
  const response = await fetch(
    `${apiUrl}/api/practices/${practiceId}/pi-calculator?year=${year}`,
    {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    }
  );
  
  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    console.error('PI Calculator save failed:', response.status, errorText);
    throw new Error(`Failed to save PI calculator data: ${response.status}`);
  }
  
  const result = await response.json();
  invalidatePracticeScoring(practiceId);
  return result;
}

/**
 * Fetch all NPI PI calculator data for a practice
 */
export async function getAllNPIPIData(
  practiceId: string,
  year: number = 2025
): Promise<PICalculatorResponse[]> {
  try {
    const apiUrl = getApiUrl();
    const token = getAuthToken();
    
    const response = await fetch(
      `${apiUrl}/api/practices/${practiceId}/pi-calculator/all-npis?year=${year}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to fetch NPI PI data: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('Error fetching NPI PI data:', error);
    return [];
  }
}

/**
 * Bulk upload NPI PI data from CSV
 */
export async function bulkUploadPIData(
  practiceId: string,
  npiData: PICalculatorData[],
  year: number = 2025
): Promise<PIBulkUploadResult | null> {
  try {
    const apiUrl = getApiUrl();
    const token = getAuthToken();
    
    const response = await fetch(
      `${apiUrl}/api/practices/${practiceId}/pi-calculator/bulk-upload?year=${year}`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ npiData }),
      }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to bulk upload PI data: ${response.status}`);
    }
    
    const result = await response.json();
    invalidatePracticeScoring(practiceId);
    return result;
  } catch (error) {
    console.error('Error bulk uploading PI data:', error);
    return null;
  }
}

/**
 * Download CSV template for bulk NPI upload
 */
export async function downloadPICSVTemplate(practiceId: string, year: number = 2025): Promise<void> {
  try {
    const apiUrl = getApiUrl();
    const token = getAuthToken();
    
    const response = await fetch(
      `${apiUrl}/api/practices/${practiceId}/pi-calculator/csv-template?year=${year}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      }
    );
    
    if (!response.ok) {
      throw new Error(`Failed to download template: ${response.status}`);
    }
    
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pi_calculator_template.csv';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  } catch (error) {
    console.error('Error downloading template:', error);
  }
}

// ============================================================================
// Client-Side Score Calculation (mirrors server-side logic for real-time UI)
// ============================================================================

/**
 * Calculate PI score on the client side for real-time updates
 * This mirrors the server-side calculation in piCalculator.js
 */
export function calculatePIScoreClient(data: PICalculatorData): PICalculatedScore {
  // Check prerequisites - if any is false or null, score is 0
  const prerequisitesPassed = 
    data.pi_pphi_1 === true &&
    data.pi_pphi_2 === true &&
    data.pi_infblo_1 === true &&
    data.pi_oncdir_1 === true;

  if (!prerequisitesPassed) {
    return {
      prerequisitesPassed: false,
      ePrescribingScore: 0,
      queryPdmpScore: 0,
      patientAccessScore: 0,
      hieScore: 0,
      publicHealthScore: 0,
      bonusPoints: 0,
      totalScore: 0,
      maxPossibleScore: 100,
      breakdown: {
        ePrescribing: { points: 0, maxPoints: 10 },
        queryPdmp: { points: 0, maxPoints: 10 },
        patientAccess: { points: 0, maxPoints: 25 },
        hie: { points: 0, maxPoints: 30 },
        publicHealth: { points: 0, maxPoints: 25 },
        bonus: { points: 0, maxPoints: 5 }
      }
    };
  }

  // Initialize point redistribution tracking
  let patientAccessMaxPoints = 25;
  let hieMaxPoints = 30;
  let ePrescribingMaxPoints = 10;
  let queryPdmpMaxPoints = 10;

  // ===== e-Prescribing Objective (20 points total) =====
  let ePrescribingScore = 0;
  let queryPdmpScore = 0;
  
  // e-Prescribing measure (10 points)
  if (data.pi_lvpp_1) {
    ePrescribingScore = 0;
    ePrescribingMaxPoints = 0;
  } else if (data.pi_ep_1_numerator != null && data.pi_ep_1_denominator != null && data.pi_ep_1_denominator > 0) {
    const rate = data.pi_ep_1_numerator / data.pi_ep_1_denominator;
    ePrescribingScore = Math.min(10, rate * 10);
  }

  // Query of PDMP (10 points)
  if (data.pi_ep_2_exclusion) {
    queryPdmpScore = 0;
    queryPdmpMaxPoints = 0;
    ePrescribingMaxPoints = data.pi_lvpp_1 ? 0 : 20;
    if (!data.pi_lvpp_1 && data.pi_ep_1_numerator != null && data.pi_ep_1_denominator != null && data.pi_ep_1_denominator > 0) {
      const rate = data.pi_ep_1_numerator / data.pi_ep_1_denominator;
      ePrescribingScore = Math.min(20, rate * 20);
    }
  } else if (data.pi_ep_2 === true) {
    queryPdmpScore = 10;
  }

  // ===== Health Information Exchange (30 points) =====
  let hieScore = 0;
  let hieSendingMaxPoints = 15;
  let hieReceivingMaxPoints = 15;
  
  if (data.pi_lvpp_1) {
    hieSendingMaxPoints += 5;
    hieReceivingMaxPoints += 5;
  }

  if (data.hie_option === 1) {
    let hieSendingScore = 0;
    let hieReceivingScore = 0;
    
    if (data.pi_lvotc_1) {
      hieSendingScore = 0;
      patientAccessMaxPoints += hieSendingMaxPoints;
      hieSendingMaxPoints = 0;
    } else if (data.pi_hie_1_numerator != null && data.pi_hie_1_denominator != null && data.pi_hie_1_denominator > 0) {
      const rate = data.pi_hie_1_numerator / data.pi_hie_1_denominator;
      hieSendingScore = Math.min(hieSendingMaxPoints, rate * hieSendingMaxPoints);
    }
    
    if (data.pi_lvitc_2) {
      hieReceivingScore = 0;
      if (!data.pi_lvotc_1) {
        hieSendingMaxPoints += hieReceivingMaxPoints;
        if (data.pi_hie_1_numerator != null && data.pi_hie_1_denominator != null && data.pi_hie_1_denominator > 0) {
          const rate = data.pi_hie_1_numerator / data.pi_hie_1_denominator;
          hieSendingScore = Math.min(hieSendingMaxPoints, rate * hieSendingMaxPoints);
        }
      } else {
        patientAccessMaxPoints += hieReceivingMaxPoints;
      }
      hieReceivingMaxPoints = 0;
    } else if (data.pi_hie_4_numerator != null && data.pi_hie_4_denominator != null && data.pi_hie_4_denominator > 0) {
      const rate = data.pi_hie_4_numerator / data.pi_hie_4_denominator;
      hieReceivingScore = Math.min(hieReceivingMaxPoints, rate * hieReceivingMaxPoints);
    }
    
    hieScore = hieSendingScore + hieReceivingScore;
    hieMaxPoints = hieSendingMaxPoints + hieReceivingMaxPoints;
  } else if (data.hie_option === 2) {
    hieScore = data.pi_hie_5 === true ? 30 : 0;
  } else if (data.hie_option === 3) {
    hieScore = data.pi_hie_6 === true ? 30 : 0;
  }

  // ===== Provider to Patient Exchange (25+ points) =====
  let patientAccessScore = 0;
  if (data.pi_pea_1_numerator != null && data.pi_pea_1_denominator != null && data.pi_pea_1_denominator > 0) {
    const rate = data.pi_pea_1_numerator / data.pi_pea_1_denominator;
    patientAccessScore = Math.min(patientAccessMaxPoints, rate * patientAccessMaxPoints);
  }

  // ===== Public Health (25 points + 5 bonus) =====
  let publicHealthScore = 0;
  let publicHealthMaxPoints = 25;
  let bonusPoints = 0;

  if (data.pi_phcdrr_1_exclusion) {
    publicHealthScore = 0;
    publicHealthMaxPoints = 0;
    patientAccessMaxPoints += 25;
    if (data.pi_pea_1_numerator != null && data.pi_pea_1_denominator != null && data.pi_pea_1_denominator > 0) {
      const rate = data.pi_pea_1_numerator / data.pi_pea_1_denominator;
      patientAccessScore = Math.min(patientAccessMaxPoints, rate * patientAccessMaxPoints);
    }
  } else if (data.pi_phcdrr_1 === true) {
    publicHealthScore = 25;
  }

  if (data.pi_phcdrr_2 === true || data.pi_phcdrr_4 === true || data.pi_phcdrr_5 === true) {
    bonusPoints = 5;
  }

  const subtotal = ePrescribingScore + queryPdmpScore + patientAccessScore + hieScore + publicHealthScore;
  const totalScore = Math.min(100, subtotal + bonusPoints);

  return {
    prerequisitesPassed: true,
    ePrescribingScore: Math.round(ePrescribingScore * 100) / 100,
    queryPdmpScore: Math.round(queryPdmpScore * 100) / 100,
    patientAccessScore: Math.round(patientAccessScore * 100) / 100,
    hieScore: Math.round(hieScore * 100) / 100,
    publicHealthScore: Math.round(publicHealthScore * 100) / 100,
    bonusPoints,
    totalScore: Math.round(totalScore * 100) / 100,
    maxPossibleScore: 100,
    breakdown: {
      ePrescribing: { points: Math.round(ePrescribingScore * 100) / 100, maxPoints: ePrescribingMaxPoints },
      queryPdmp: { points: Math.round(queryPdmpScore * 100) / 100, maxPoints: queryPdmpMaxPoints },
      patientAccess: { points: Math.round(patientAccessScore * 100) / 100, maxPoints: patientAccessMaxPoints },
      hie: { points: Math.round(hieScore * 100) / 100, maxPoints: hieMaxPoints },
      publicHealth: { points: Math.round(publicHealthScore * 100) / 100, maxPoints: publicHealthMaxPoints },
      bonus: { points: bonusPoints, maxPoints: 5 }
    }
  };
}

// ============================================================================
// Validation Functions
// ============================================================================

export interface PIValidationError {
  field: string;
  message: string;
}

/**
 * Validate performance period (minimum 180 days)
 */
export function validatePerformancePeriod(
  startDate: string | null | undefined,
  endDate: string | null | undefined
): PIValidationError | null {
  if (!startDate || !endDate) return null; // Not required until submission
  
  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffTime = Math.abs(end.getTime() - start.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays < 180) {
    return {
      field: 'performance_period',
      message: 'Performance period must be at least 180 consecutive days'
    };
  }
  
  // Check start date is not after July 5 of the period's year (allows 180-day period)
  const periodYear = start.getFullYear();
  const latestStart = new Date(periodYear, 6, 5); // July 5 of the entered year
  if (start > latestStart) {
    return {
      field: 'performance_start',
      message: `Start date cannot be after July 5, ${periodYear} (to allow 180-day period)`
    };
  }
  
  return null;
}

/**
 * Validate numerator/denominator relationship
 */
export function validateNumeratorDenominator(
  numerator: number | null | undefined,
  denominator: number | null | undefined,
  fieldName: string
): PIValidationError | null {
  if (numerator == null || denominator == null) return null;
  
  if (numerator > denominator) {
    return {
      field: fieldName,
      message: 'Numerator cannot exceed denominator'
    };
  }
  
  if (denominator === 0) {
    return {
      field: fieldName,
      message: 'Denominator must be greater than 0. If no patients qualify, claim an exclusion.'
    };
  }
  
  return null;
}

/**
 * Validate e-Prescribing and PDMP exclusion dependency
 */
export function validateEPrescribingExclusions(data: PICalculatorData): PIValidationError | null {
  if (data.pi_lvpp_1 && !data.pi_ep_2_exclusion) {
    return {
      field: 'pi_ep_2_exclusion',
      message: 'If claiming e-Prescribing exclusion, you must also claim a Query of PDMP exclusion'
    };
  }
  return null;
}

/**
 * Get all validation errors for the current data
 */
export function getAllValidationErrors(data: PICalculatorData): PIValidationError[] {
  const errors: PIValidationError[] = [];
  
  // Performance period
  const periodError = validatePerformancePeriod(data.performance_start, data.performance_end);
  if (periodError) errors.push(periodError);
  
  // Numerator/Denominator validations
  const epError = validateNumeratorDenominator(
    data.pi_ep_1_numerator,
    data.pi_ep_1_denominator,
    'pi_ep_1'
  );
  if (epError) errors.push(epError);
  
  const peaError = validateNumeratorDenominator(
    data.pi_pea_1_numerator,
    data.pi_pea_1_denominator,
    'pi_pea_1'
  );
  if (peaError) errors.push(peaError);
  
  const hie1Error = validateNumeratorDenominator(
    data.pi_hie_1_numerator,
    data.pi_hie_1_denominator,
    'pi_hie_1'
  );
  if (hie1Error) errors.push(hie1Error);
  
  const hie4Error = validateNumeratorDenominator(
    data.pi_hie_4_numerator,
    data.pi_hie_4_denominator,
    'pi_hie_4'
  );
  if (hie4Error) errors.push(hie4Error);
  
  // e-Prescribing exclusion dependency
  const epExclusionError = validateEPrescribingExclusions(data);
  if (epExclusionError) errors.push(epExclusionError);
  
  return errors;
}

// ============================================================================
// CSV Parsing Functions
// ============================================================================

/**
 * Split one CSV line into fields, honoring double-quoted values
 * (commas inside quotes, and "" as an escaped quote).
 */
function splitCSVLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    if (inQuotes) {
      if (char === '"') {
        if (line[i + 1] === '"') {
          current += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else if (char === '"') {
      inQuotes = true;
    } else if (char === ',') {
      fields.push(current);
      current = '';
    } else {
      current += char;
    }
  }
  fields.push(current);
  return fields;
}

/**
 * Parse CSV content into PICalculatorData array
 */
export function parseCSVToPIData(csvContent: string): PICalculatorData[] {
  const lines = csvContent.trim().split(/\r?\n/);
  if (lines.length < 2) return [];
  
  const headers = splitCSVLine(lines[0]).map(h => h.trim().toLowerCase());
  const data: PICalculatorData[] = [];
  
  for (let i = 1; i < lines.length; i++) {
    const values = splitCSVLine(lines[i]).map(v => v.trim());
    const record: PICalculatorData = {};
    
    headers.forEach((header, index) => {
      const value = values[index];
      if (!value || value === '') return;
      
      // Parse based on field type
      if (header === 'npi' || header === 'cehrt_id') {
        (record as any)[header] = value;
      } else if (header.includes('_numerator') || header.includes('_denominator') || header === 'hie_option') {
        (record as any)[header] = parseInt(value, 10);
      } else if (header.includes('_level') || header.includes('_exclusion')) {
        (record as any)[header] = value || null;
      } else if (header.includes('_start') || header.includes('_end')) {
        (record as any)[header] = value;
      } else {
        // Boolean fields
        (record as any)[header] = value.toLowerCase() === 'true';
      }
    });
    
    if (record.npi) {
      data.push(record);
    }
  }
  
  return data;
}

/**
 * Convert PICalculatorData array to CSV string
 */
export function piDataToCSV(data: PICalculatorData[]): string {
  const headers = [
    'npi',
    'performance_start',
    'performance_end',
    'cehrt_id',
    'pi_pphi_1',
    'pi_pphi_2',
    'pi_infblo_1',
    'pi_oncdir_1',
    'pi_ep_1_numerator',
    'pi_ep_1_denominator',
    'pi_lvpp_1',
    'pi_ep_2',
    'pi_ep_2_exclusion',
    'pi_pea_1_numerator',
    'pi_pea_1_denominator',
    'hie_option',
    'pi_hie_1_numerator',
    'pi_hie_1_denominator',
    'pi_lvotc_1',
    'pi_hie_4_numerator',
    'pi_hie_4_denominator',
    'pi_lvitc_2',
    'pi_hie_5',
    'pi_hie_6',
    'pi_phcdrr_1',
    'pi_phcdrr_1_level',
    'pi_phcdrr_1_exclusion',
    'pi_phcdrr_2',
    'pi_phcdrr_2_level',
    'pi_phcdrr_4',
    'pi_phcdrr_4_level',
    'pi_phcdrr_5',
    'pi_phcdrr_5_level'
  ];
  
  const rows = data.map(record => {
    return headers.map(header => {
      const value = (record as any)[header];
      if (value === null || value === undefined) return '';
      if (typeof value === 'boolean') return value.toString();
      return value.toString();
    }).join(',');
  });
  
  return [headers.join(','), ...rows].join('\n');
}

